import React, { useState } from 'react';
import NumberFlow from '@number-flow/react';
import { motion } from 'framer-motion';
import { Calculator, DollarSign, Clock, Users } from 'lucide-react';

export const ROICalculatorSection = () => {
    const [patientsPerMonth, setPatientsPerMonth] = useState(500);
    const [minutesPerPatient, setMinutesPerPatient] = useState(15);
    const [hourlyWage, setHourlyWage] = useState(45);

    // Calculations
    const hoursSavedPerMonth = (patientsPerMonth * minutesPerPatient) / 60;
    const hoursSavedPerYear = hoursSavedPerMonth * 12;
    const costSavedPerYear = hoursSavedPerYear * hourlyWage;

    return (
        <section className="py-24 bg-slate-50 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-200/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-200/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-4">
                        <Calculator className="w-4 h-4" />
                        <span>ROI Calculator</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-['Instrument_Serif'] text-slate-900 mb-6">
                        Estimate Your ROI
                    </h2>
                    <p className="text-xl text-slate-600 font-light">
                        Quantify the impact of integrating Cura's API into your healthcare workflow.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
                    {/* Controls */}
                    <div className="lg:col-span-7 bg-white rounded-2xl shadow-xl border border-slate-100 p-8 h-full flex flex-col justify-center">
                        <h3 className="text-xl font-semibold text-slate-900 mb-8">Input Your Metrics</h3>

                        <div className="space-y-10">
                            {/* Slider 1 */}
                            <div>
                                <div className="flex justify-between items-end mb-4">
                                    <label className="flex items-center gap-2 text-slate-700 font-medium mb-1">
                                        <Users className="w-5 h-5 text-blue-500" />
                                        Monthly Patient Interactions
                                    </label>
                                    <div className="flex items-baseline gap-1">
                                        <input
                                            type="number"
                                            value={patientsPerMonth}
                                            onChange={(e) => setPatientsPerMonth(Number(e.target.value))}
                                            className="w-28 text-right text-3xl font-serif font-bold text-slate-900 bg-transparent border-none focus:ring-0 p-0 focus:bg-slate-50 rounded px-2 transition-all cursor-text hover:bg-slate-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        />
                                    </div>
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max="10000"
                                    step="50"
                                    value={patientsPerMonth}
                                    onChange={(e) => setPatientsPerMonth(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                                />
                            </div>

                            {/* Slider 2 */}
                            <div>
                                <div className="flex justify-between items-end mb-4">
                                    <label className="flex items-center gap-2 text-slate-700 font-medium mb-1">
                                        <Clock className="w-5 h-5 text-purple-500" />
                                        Manual Triage Time (Minutes)
                                    </label>
                                    <div className="flex items-baseline gap-1">
                                        <input
                                            type="number"
                                            value={minutesPerPatient}
                                            onChange={(e) => setMinutesPerPatient(Number(e.target.value))}
                                            className="w-20 text-right text-3xl font-serif font-bold text-slate-900 bg-transparent border-none focus:ring-0 p-0 focus:bg-slate-50 rounded px-2 transition-all cursor-text hover:bg-slate-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        />
                                        <span className="text-slate-500 font-medium">min</span>
                                    </div>
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max="120"
                                    step="1"
                                    value={minutesPerPatient}
                                    onChange={(e) => setMinutesPerPatient(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                                />
                            </div>

                            {/* Slider 3 */}
                            <div>
                                <div className="flex justify-between items-end mb-4">
                                    <label className="flex items-center gap-2 text-slate-700 font-medium mb-1">
                                        <DollarSign className="w-5 h-5 text-emerald-500" />
                                        Clinician Hourly Cost
                                    </label>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-3xl font-bold text-slate-900 font-serif">$</span>
                                        <input
                                            type="number"
                                            value={hourlyWage}
                                            onChange={(e) => setHourlyWage(Number(e.target.value))}
                                            className="w-24 text-right text-3xl font-serif font-bold text-slate-900 bg-transparent border-none focus:ring-0 p-0 focus:bg-slate-50 rounded px-2 transition-all cursor-text hover:bg-slate-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        />
                                    </div>
                                </div>
                                <input
                                    type="range"
                                    min="0"
                                    max="500"
                                    step="5"
                                    value={hourlyWage}
                                    onChange={(e) => setHourlyWage(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Results Card */}
                    <div className="lg:col-span-5 flex flex-col h-full gap-6">
                        <motion.div
                            className="bg-gradient-to-br from-[#003c83] to-[#002d62] rounded-2xl shadow-2xl p-8 text-white relative overflow-hidden shrink-0"
                            whileHover={{ scale: 1.02 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="absolute top-0 right-0 p-4 opacity-10">
                                <DollarSign className="w-32 h-32" />
                            </div>

                            <h4 className="text-blue-100 font-medium mb-2">Estimated Operational Savings</h4>
                            <div className="text-4xl md:text-5xl font-serif font-bold mb-6 flex items-baseline">
                                <span className="text-2xl mr-1">$</span>
                                <NumberFlow
                                    value={costSavedPerYear}
                                    format={{ notation: 'compact', compactDisplay: 'short', maximumFractionDigits: 1 }}
                                />
                                <span className="text-lg ml-2 font-sans font-normal opacity-80">/ year</span>
                            </div>

                            <div className="h-px bg-blue-400/30 w-full my-6" />

                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm">
                                    <Clock className="w-6 h-6 text-blue-100" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold font-serif">
                                        <NumberFlow value={hoursSavedPerYear} />
                                    </div>
                                    <div className="text-blue-200 text-sm">Clinical Hours Reclaimed</div>
                                </div>
                            </div>
                        </motion.div>

                        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 flex-grow flex flex-col justify-center">
                            <h4 className="font-semibold text-slate-900 mb-4">Strategic Impact</h4>
                            <ul className="space-y-4">
                                <li className="flex items-start gap-3 text-slate-600 text-sm">
                                    <div className="mt-1 w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                                    <span>Reduce administrative burnout and staff turnover</span>
                                </li>
                                <li className="flex items-start gap-3 text-slate-600 text-sm">
                                    <div className="mt-1 w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                                    <span>Reallocate <strong className="text-slate-900">{Math.round(hoursSavedPerMonth)} hours/month</strong> to direct patient care</span>
                                </li>
                                <li className="flex items-start gap-3 text-slate-600 text-sm">
                                    <div className="mt-1 w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                                    <span>Minimize diagnostic coding errors and claim denials</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
